import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Button,
  Modal,
  Input,
  Form,
  Space,
  Typography,
  message,
} from 'antd';
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import ProductStatusFilter from 'app/shared/components/ProductStatusFilter';
import type { ProductStatusFilterValue } from 'app/shared/components/productStatusFilterUtils';
import { matchesProductStatusFilter } from 'app/shared/components/productStatusFilterUtils';
import ManagerSearchByName from 'app/shared/components/ManagerSearchByName';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  AccountCategory,
  fetchAccountCategories,
  fetchAccountCategoryById,
  saveAccountCategory,
  openModal,
  closeModal,
  setFilter,
} from './actions';

import {
  getFilteredAccountList,
  getAccountLoading,
  getAccountSaving,
  getFilterName,
  getModalOpen,
  getEditingItem,
  getAccountList,
} from './selectors';

import './Page.css';

const { Title } = Typography;

const AccountManagerPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const loading = useSelector(getAccountLoading);
  const saving = useSelector(getAccountSaving);
  const list = useSelector(getAccountList);
  const filtered = useSelector(getFilteredAccountList);
  const filterName = useSelector(getFilterName);
  const [activeStatusFilter, setActiveStatusFilter] = useState<ProductStatusFilterValue>('all');
  const modalOpen = useSelector(getModalOpen);
  const editing = useSelector(getEditingItem);
  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(fetchAccountCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!modalOpen) return;

    if (!editing) {
      form.resetFields();
      form.setFieldsValue({ name: '', active: true });
      return;
    }

    let cancelled = false;
    (async () => {
      const fresh = await dispatch(fetchAccountCategoryById(editing.id));
      if (cancelled) return;
      form.setFieldsValue({
        name: fresh?.name ?? editing.name,
        active: typeof fresh?.active === 'boolean' ? fresh.active : editing.active,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [modalOpen, editing, dispatch, form]);

  const handleAdd = () => {
    dispatch(openModal(null));
  };

  const handleOpenEdit = (record: AccountCategory) => {
    dispatch(openModal(record));
  };

  const uniqueNameValidator = async (_: any, value?: string) => {
    const name = (value ?? '').trim().toLowerCase();
    if (!name) return Promise.resolve();
    const currentId = editing?.id;
    const exists = list.some(
      c => c.name.trim().toLowerCase() === name && c.id !== currentId
    );
    if (exists) return Promise.reject(new Error('Account Category Name must be unique.'));
    return Promise.resolve();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        id: editing?.id,
        name: values.name.trim(),
        active: !!values.active,
      };
      const res = await dispatch(saveAccountCategory(payload));
      if (res.ok) {
        message.success(editing ? 'Category updated successfully.' : 'Category created successfully.');
        dispatch(closeModal());
        form.resetFields();
      } else {
        message.error(res.message || 'Failed to save category.');
      }
    } catch (err: any) {
      if (!err?.errorFields) {
        message.error(err?.message || 'Failed to save category.');
      }
    }
  };

const columns: ColumnsType<AccountCategory> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 40,
      render: (id: number) => <span>{id || '-'}</span>,
      sorter: (a: AccountCategory, b: AccountCategory) => a.id - b.id,
    },
    {
      title: 'Account Category Name',
      dataIndex: 'name',
      width: 200,
      key: 'name',
      render: (_: any, record: AccountCategory) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => handleOpenEdit(record)}>
          {record.name}
        </Button>
      ),
      sorter: (a: AccountCategory, b: AccountCategory) =>
        (a.name ?? '').localeCompare(b.name ?? ''),
    },

    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 120,
      render: (text: string) => <span>{text || '-'}</span>,
    },
    {
      title: 'Last Modified By',
      dataIndex: 'lastModifiedBy',
      key: 'lastModifiedBy',
      width: 120,
      render: (text: string) => <span>{text || '-'}</span>,
    },

    {
      title: 'Active',
      dataIndex: 'active',
      key: 'active',
      align: 'center',
      width: 40,
      render: (val: boolean) =>
        val ? <span style={{ color: 'green' }}>Yes</span> : <span style={{ color: 'red' }}>No</span>,
    },
  ];

  const displayed = useMemo(
    () => filtered.filter((item) => matchesProductStatusFilter(item.active, activeStatusFilter)),
    [filtered, activeStatusFilter]
  );

  return (
    <>
      <Title level={3} style={{ margin: 0, marginBottom: 16 }}>Account Category Manager</Title>

      <Space style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', width: '100%' }}>
        <Space>
          <ManagerSearchByName value={filterName} onChange={(v) => dispatch(setFilter(v))} />
          <ProductStatusFilter
            value={activeStatusFilter}
            onChange={setActiveStatusFilter}
          />
        </Space>

        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add New
        </Button>
      </Space>

      <Table<AccountCategory>
        rowKey="id"
        loading={loading}
        dataSource={displayed}
        columns={columns}
        pagination={{ pageSize: 10, showSizeChanger: false }}
        rowClassName={(record) => (!record.active ? 'inactive-row' : '')}
      />

      <Modal
        open={modalOpen}
        title={editing ? 'Edit Account Category' : 'Add Account Category'}
        onCancel={() => { dispatch(closeModal()); form.resetFields(); }}
        onOk={handleSave}
        okText="Save"
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Account Category Name"
            name="name"
            rules={[
              { required: true, message: 'Please enter a category name' },
              { validator: uniqueNameValidator },
            ]}
          >
            <Input maxLength={60} placeholder="Enter unique category name" />
          </Form.Item>
          <ActiveStatusFormItem name="active" initialValue={true} />
        </Form>
      </Modal>
    </>
  );
};

export default AccountManagerPage;